import { useFormaliteService, useOffreService } from '@europrocurement/l2d-domain';
import { PrestationJsonldPrestationRead } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { useOptionComplementaire } from '@europrocurement/l2d-modules/hooks';
import { useFormalitiesCtx, useFormulairesCtx } from '@europrocurement/l2d-modules/providers';
import { castIriToId } from '@europrocurement/l2d-utils';

export type DossierPrestation = {
    label: string;
    dossierId: number;
    formaliteId: number;
    formaliteSortOrder: number;
    catalogueId: number;
    prestationId: number;
    prestationJson: object;
    complete: boolean;
    deleted: boolean;
};

export type DossierFormulaire = DossierPrestation & {
    formulaireId?: number;
    formulaireJson?: unknown;
};

const useDossierFormulaire = () => {
    const { prestationFormaliteApi } = useFormaliteService();
    const { catalogsApi } = useOffreService();
    const { getFormalityById } = useFormalitiesCtx();
    const { getFormulaireById } = useFormulairesCtx();
    const { catalogueOptionsToFormulaire } = useOptionComplementaire();

    const prestationTransformer = (
        prestation: PrestationJsonldPrestationRead,
    ): DossierPrestation => {
        const formalite = getFormalityById(castIriToId(prestation?.formalite as string));

        return {
            label: prestation.libelle as string,
            dossierId: castIriToId(prestation?.dossier as string),
            formaliteId: formalite?.id as number,
            formaliteSortOrder: formalite?.sortOrder || Infinity,
            catalogueId: prestation?.catalogue?.id as number,
            prestationId: prestation.id as number,
            prestationJson: JSON.parse(prestation.json || '{}'),
            complete: false,
            deleted: prestation.deleted as boolean,
        };
    };

    const getDossierPrestations = async (dossierId: number): Promise<Array<DossierPrestation>> => {
        const requestParameters = { dossier: dossierId as unknown as string };
        const result =
            await prestationFormaliteApi.apiDossiersDossierPrestationsGetCollection(
                requestParameters,
            );

        return result.data['hydra:member'].map(prestationTransformer);
    };

    const getFormaliteFormulaire = async (dossierId: number): Promise<Array<DossierFormulaire>> => {
        const prestations = await getDossierPrestations(dossierId);
        const promises = prestations.map(async (prestation) => {
            const formalite = getFormalityById(prestation.formaliteId);

            if (!formalite?.formId) {
                return prestation;
            }

            const formulaire = getFormulaireById(formalite.formId);
            const formulaireJson = formulaire?.formulaire;
            const catalog = await catalogsApi.apiCataloguesIdGet({
                id: prestation.catalogueId as unknown as string,
            });
            const options = await catalogueOptionsToFormulaire(catalog?.data?.options || []);

            if (options && formulaireJson) {
                formulaireJson.pages.push(options);
            }

            return {
                ...prestation,
                formulaireId: formalite.formId,
                formulaireJson,
            };
        });

        const result = await Promise.all(promises);

        return result.flat();
    };

    const setDossierPrestation = async (
        formulaire: DossierFormulaire,
    ): Promise<DossierFormulaire> => {
        const result = await prestationFormaliteApi.apiPrestationsIdPut({
            id: formulaire.prestationId as unknown as string,
            prestationJsonldPrestationUpdate: {
                libelle: formulaire.label,
                json: JSON.stringify(formulaire.prestationJson),
            },
        });

        return {
            ...prestationTransformer(result.data),
            formulaireId: formulaire.formulaireId,
            formulaireJson: formulaire.formulaireJson,
        };
    };

    return { getFormaliteFormulaire, setDossierPrestation };
};

export default useDossierFormulaire;
